import { useCallback, useMemo } from 'react'
import { styled } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { SelectedChip } from './CollapsibleSelectField'
import { useSortableList } from './hooks/useSortableList'
import { DND_AUTO_SCROLL_CONFIG, DND_MEASURING_CONFIG } from './dndConfig'
import { EntityIcon } from '../../EntityIcon'
import { colorPalette } from '../../../../../style/colorPalette'
import { intl } from '../../../../../../i18n'

type SortableChipListProps = {
  chips: SelectedChip[]
  /** 並び替えたあとのチップの key の並び */
  onReorder: (keys: string[]) => void
  onRemove: (key: string) => void
}

/** 選択済みのチップを、ドラッグで並び替えられ、× で外せる形で並べる */
export const SortableChipList = ({
  chips,
  onReorder,
  onRemove,
}: SortableChipListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  )

  const { handleDragStart, handleDragEnd, handleDragCancel } = useSortableList({
    items: chips,
    onReorder: reorderedChips =>
      onReorder(reorderedChips.map(chip => chip.key)),
    getId: chip => chip.key,
  })

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      measuring={DND_MEASURING_CONFIG}
      autoScroll={DND_AUTO_SCROLL_CONFIG}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext
        items={chips.map(chip => chip.key)}
        strategy={rectSortingStrategy}
      >
        {chips.map(chip => (
          <SortableChip
            key={chip.key}
            id={chip.key}
            label={chip.label}
            iconUrl={chip.iconUrl}
            onRemove={onRemove}
          />
        ))}
      </SortableContext>
    </DndContext>
  )
}

type SortableChipProps = {
  id: string
  label: string
  iconUrl?: string
  onRemove: (id: string) => void
}

// 折りたたみ表示のチップ自体をドラッグ&ドロップの対象にし、ホバー時のみ削除ボタンを表示する
const SortableChip = ({ id, label, iconUrl, onRemove }: SortableChipProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = useMemo(
    () => ({ transform: CSS.Transform.toString(transform), transition }),
    [transform, transition]
  )

  const handleRemoveClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onRemove(id)
    },
    [id, onRemove]
  )

  return (
    <ChipRoot
      ref={setNodeRef}
      style={style}
      isDragging={isDragging}
      {...attributes}
      {...listeners}
    >
      <EntityIcon src={iconUrl} size={18} />
      <ChipLabel>{label}</ChipLabel>
      <ChipRemoveButton
        type="button"
        className="chip-remove-button"
        onClick={handleRemoveClick}
        aria-label={intl.formatMessage({
          id: 'versionReport.treeSelect.remove',
        })}
      >
        <CloseRoundedIcon sx={{ fontSize: 13 }} />
      </ChipRemoveButton>
    </ChipRoot>
  )
}

const ChipRoot = styled('div', {
  shouldForwardProp: prop => prop !== 'isDragging',
})<{ isDragging?: boolean }>(({ isDragging }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  background: colorPalette.monotone[1],
  color: colorPalette.monotone[5],
  borderRadius: '15px',
  padding: '3px 6px 3px 9px',
  fontSize: '12px',
  cursor: 'grab',
  touchAction: 'none',
  opacity: isDragging ? 0.5 : 1,
  zIndex: isDragging ? 1 : 'auto',
  position: 'relative',
  '&:hover .chip-remove-button': {
    opacity: 1,
  },
}))

const ChipLabel = styled('span')({
  maxWidth: '160px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const ChipRemoveButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '16px',
  height: '16px',
  padding: 0,
  border: 'none',
  borderRadius: '50%',
  background: 'transparent',
  color: colorPalette.monotone[4],
  cursor: 'pointer',
  opacity: 0,
  transition: 'opacity 0.12s ease, background 0.12s ease',
  flexShrink: 0,
  '&:hover': {
    background: colorPalette.monotone[2],
  },
})
